import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations } from '../i18n/translations';

const LanguageContext = createContext(null);
const STORAGE_KEY = 'bcg_lang';
const SUPPORTED = ['en', 'fr'];

// Default: previously chosen language, otherwise the browser's preference.
const getInitialLang = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (SUPPORTED.includes(saved)) return saved;
  } catch { /* localStorage may be unavailable */ }

  const candidates = [
    ...(navigator.languages || []),
    navigator.language,
  ].filter(Boolean);
  const prefersFrench = candidates.some(l => l.toLowerCase().startsWith('fr'));
  return prefersFrench ? 'fr' : 'en';
};

// Resolve a dotted key ("ventures.club.title") against a dictionary.
const resolve = (dict, key) =>
  key.split('.').reduce((obj, part) => (obj == null ? undefined : obj[part]), dict);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(getInitialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED.includes(next)) setLangState(next);
  }, []);

  // Translate a dotted key; falls back to English, then the key itself.
  const t = useCallback((key) => {
    const value = resolve(translations[lang], key);
    if (value != null) return value;
    const fallback = resolve(translations.en, key);
    return fallback != null ? fallback : key;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
};
